// src/game/story/effects.js
// Tiny declarative effect runner + requirements checker.
// Keep this small and additive; story content stays data-driven.

function effectPassesChance(effect) {
  const chance = Number(effect?.chance);
  if (!Number.isFinite(chance)) return true;
  if (chance <= 0) return false;
  if (chance <= 1) return Math.random() < chance;
  if (chance >= 100) return true;
  return Math.random() * 100 < chance;
}

export function applyEffects(effects, ctx) {
  if (!Array.isArray(effects)) return { startedCombat: false, combatHasResume: false };

  const meta = {
    startedCombat: false,
    combatHasResume: false,
  };

  for (const e of effects) {
    if (!e || !e.type) continue;
    if (!effectPassesChance(e)) continue;

    switch (e.type) {
      case 'sanity': {
        const d = Number(e.delta) || 0;
        ctx.addSanity?.(d);
        break;
      }

      case 'resource': {
        const key = String(e.key || '');
        const d = Number(e.delta) || 0;
        ctx.addResource?.(key, d);
        break;
      }

      case 'flag': {
        const key = String(e.key || '');
        if (!key) break;
        ctx.setFlag?.(key, e.value);
        break;
      }

      case 'gainTrinket': {
        const relicId = e.relicId || e.id;
        const count = Math.max(1, Math.floor(Number(e.count) || 1));
        for (let i = 0; i < count; i += 1) {
          ctx.gainTrinketById?.(relicId);
        }
        break;
      }

      case 'gainTrinketRandom': {
        ctx.gainRandomTrinket?.({
          count: Math.max(1, Math.floor(Number(e.count) || 1)),
          pool: e.pool,
        });
        break;
      }

      case 'addCardToDeck': {
        const cardId = e.cardId || e.id;
        const count = Math.max(1, Math.floor(Number(e.count) || 1));
        for (let i = 0; i < count; i += 1) {
          ctx.addCardToDeck?.(cardId);
        }
        break;
      }

      case 'gainCardRandom': {
        ctx.gainRandomCard?.({
          count: Math.max(1, Math.floor(Number(e.count) || 1)),
          pool: e.pool,
        });
        break;
      }

      case 'startCombat':
      case 'combat': {
        const result = ctx.startCombat?.({
          encounterId: e.encounterId || e.enemyId,
          difficulty: e.difficulty,
          resumeStorylet: e.resumeStorylet,
          resumeScene: e.resumeScene,
        });
        if (result?.started) meta.startedCombat = true;
        if (result?.hasResume) meta.combatHasResume = true;
        break;
      }

      case 'loot': {
        ctx.grantLoot?.({ table: e.table, tier: e.tier, count: e.count });
        break;
      }

      default:
        break;
    }
  }

  return meta;
}

export function checkReqs(reqs, { flags, resources, meta } = {}) {
  if (!Array.isArray(reqs) || reqs.length === 0) return true;

  for (const r of reqs) {
    if (!r || !r.type) continue;

    if (r.type === 'flag') {
      const key = String(r.key || '');
      if ((flags?.[key] ?? false) !== r.eq) return false;
      continue;
    }

    if (r.type === 'hasResource') {
      const key = String(r.key || '');
      const gte = Number(r.gte) || 0;
      const have = Number(resources?.[key]) || 0;
      if (have < gte) return false;
      continue;
    }

    if (r.type === 'floorGte') {
      const gte = Number(r.gte) || 0;
      const floor = Number(meta?.floor) || 0;
      if (floor < gte) return false;
      continue;
    }

    if (r.type === 'biomeIs') {
      const want = String(r.value || '');
      const biome = String(meta?.biome || '');
      if (want && biome && want !== biome) return false;
      continue;
    }
  }

  return true;
}
